<template>
  <div
    id="filter-container"
    class="filter-container d-flex justify-end align-center flex-wrap pa-2"
  >
    <v-chip
      v-if="numberOfFiltersApplied"
      class="ma-2"
      closable
      color="primary"
      label
      variant="outlined"
      @click:close="onClearFilter"
      @click="isFilterMenuOpen = true"
    >
      {{ numberOfFiltersApplied }} filter{{ numberOfFiltersApplied > 1 ? 's' : '' }} applied
    </v-chip>
    <v-menu
      id="filter-menu"
      v-model="isFilterMenuOpen"
      :close-on-content-click="false"
      transition="slide-y-transition"
      location="bottom right"
      :offset="10"
    >
      <template #activator="{ props }">
        <cs-button primary label="Filter" v-bind="props" right-icon="mdi-filter"></cs-button>
      </template>

      <v-card>
        <v-list>
          <v-list-item>
            <v-tabs height="40" color="primary" grow>
              <v-tab>Filter by</v-tab>
            </v-tabs>
          </v-list-item>
        </v-list>

        <v-list class="filter-input-container">
          <v-list-item>
            <autocomplete-filter-input
              :multiple="true"
              :input-label="'Name'"
              :items="clientNameOptionItems"
              :input-value="filterInput.clientName"
              :on-value-change="setFilterInput('clientName')"
              :number-of-selected-items-to-display="1"
              variant="outlined"
            ></autocomplete-filter-input>
          </v-list-item>

          <v-list-item>
            <autocomplete-filter-input
              :multiple="true"
              :input-label="'Value'"
              :items="contextValueOptionItems"
              :input-value="filterInput.contextKeyValue"
              :on-value-change="setFilterInput('contextKeyValue')"
              :number-of-selected-items-to-display="1"
              variant="outlined"
            ></autocomplete-filter-input>
          </v-list-item>

          <v-list-item>
            <autocomplete-filter-input
              :multiple="false"
              :input-label="'Lock on Child Site'"
              :items="lockStatusOptionItems"
              :input-value="filterInput.isChildSiteLocked"
              :on-value-change="setFilterInput('isChildSiteLocked')"
              variant="outlined"
            ></autocomplete-filter-input>
          </v-list-item>

          <v-list-item>
            <autocomplete-filter-input
              :multiple="false"
              :input-label="'Status'"
              :items="valueStatusOptionItems"
              :input-value="filterInput.childUpdateStatus"
              :on-value-change="setFilterInput('childUpdateStatus')"
              variant="outlined"
            ></autocomplete-filter-input>
          </v-list-item>
        </v-list>

        <v-card-actions>
          <v-spacer></v-spacer>
          <div class="filter-action ma-2">
            <cs-button
              id="clear-all-button"
              class="mr-2"
              label="Clear All"
              elevation="0"
              @click="onClearFilter"
            ></cs-button>
            <cs-button
              id="apply-filter-button"
              elevation="0"
              color="primary"
              label="Apply Filters"
              @click="onApplyFilter"
            >
            </cs-button>
          </div>
        </v-card-actions>
      </v-card>
    </v-menu>
  </div>
</template>
<script>
import { CSBase } from '@complispace/cs-design-system';
import AutocompleteFilterInput from '@/components/content/filter-input/AutocompleteFilterInput';
import markdownIt from 'markdown-it';
import { markdownItFancyListPlugin } from 'markdown-it-fancy-lists';
import { defaultContextKeyFilter, lockStatusOptionItems, valueStatusOptionItems } from './util';

const md = markdownIt({
  html: true
});

md.use(markdownItFancyListPlugin);

export default {
  name: 'ContextKeyFilter',

  components: {
    AutocompleteFilterInput
  },
  extends: CSBase,

  props: {
    items: {
      type: Array,
      required: true
    },
    filter: {
      type: Object,
      default: { ...defaultContextKeyFilter }
    },
    setFilter: {
      type: Function
    }
  },
  data() {
    return {
      isFilterMenuOpen: false,
      filterInput: { ...defaultContextKeyFilter }
    };
  },

  computed: {
    clientNameOptionItems() {
      return this.items.map((item) => ({
        text: item.clientName,
        value: item.clientName
      }));
    },

    contextValueOptionItems() {
      const checkContextKeyValueIsEmpty = (contextKeyValue) => {
        return !contextKeyValue || contextKeyValue.trim() === '';
      };

      const sanitiseDisplayText = (contextKeyValue) => {
        const isEmptyContextKeyValue = checkContextKeyValueIsEmpty(contextKeyValue);
        if (isEmptyContextKeyValue) {
          return '(No Value)';
        }

        const replacementRegex = /<(img).*?>|<[^>]*>?/gm;
        const htmlContextKeyValue = this.mdToHtml(contextKeyValue);
        return htmlContextKeyValue
          .replace(replacementRegex, (match, p1) => {
            if (p1 === 'img') {
              return '[Image]';
            }
            return '';
          })
          .replace(/[^a-zA-Z0-9[\]\s]/g, ''); // remove all html tags and replace img tags with [Image]
      };

      const uniqueItems = new Set();
      return this.items
        .sort((item) => {
          return !item.contextKeyValue ? -1 : 1;
        })
        .map((item) => ({
          text: sanitiseDisplayText(item.contextKeyValue),
          value: checkContextKeyValueIsEmpty(item.contextKeyValue) ? '' : item.contextKeyValue
        }))
        .filter((item) => {
          // vuetify v3 autocomplete now shows all items, so we need to filter out duplicates
          const isUnique = !uniqueItems.has(item.text);
          if (isUnique) {
            uniqueItems.add(item.text);
            uniqueItems.add(item.value);
          }
          return isUnique;
        });
    },
    numberOfFiltersApplied() {
      return Object.keys(this.filter).filter((key) => this.filter[key].length > 0).length;
    }
  },

  watch: {
    isFilterMenuOpen(newValue) {
      if (newValue === true) {
        this.filterInput = this.filter;
      }
    },
    filter(initialFilter) {
      this.filterInput = initialFilter;
    }
  },
  created() {
    this.lockStatusOptionItems = lockStatusOptionItems;
    this.valueStatusOptionItems = valueStatusOptionItems;
  },

  methods: {
    closeFilterMenu() {
      this.isFilterMenuOpen = false;
    },
    setFilterInput(filterKey) {
      return (filterValue) => {
        this.filterInput = {
          ...this.filterInput,
          [filterKey]: filterValue
        };
      };
    },
    onApplyFilter() {
      this.setFilter(this.filterInput);
      this.closeFilterMenu();
    },
    onClearFilter() {
      this.filterInput = { ...defaultContextKeyFilter };
      this.setFilter({ ...defaultContextKeyFilter });
    },
    mdToHtml(content) {
      if (!content) return null;
      return md.render(content);
    }
  }
};
</script>

<style scoped>
.filter-container {
  gap: 10px 10px;
}
.filter-input-container {
  width: 400px;
  overflow: auto;
  max-height: 400px;
}
.filter-action {
  display: flex;
  gap: 10px 10px;
}
.filter-input-container :deep(.v-list-item) {
  margin-bottom: 16px;
}
.filter-input-container :deep(.v-list-item__content) {
  overflow: visible;
}
</style>
